import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../context/AuthContext';
import { queryCache, CACHE_TTL } from '../lib/cache';

export interface WinnerInfo {
  id: string;
  firstName: string;
  lastName: string;
}

export interface CompletedCompetition {
  id: string;
  name: string;
  competitionType: 'lms' | 'predictor' | 'worldcup';
  completionType: 'single_winner' | 'pot_split' | 'rollover' | null;
  winners: WinnerInfo[];
  finalGameweek: number | null;
  completedAt: Date;
  prizePot?: number;
  prizeCurrency?: string;
}

function normalizeCompletedCompetitionType(
  competitionType: string | null | undefined,
): CompletedCompetition['competitionType'] {
  if (competitionType === 'predictor' || competitionType === 'worldcup') {
    return competitionType;
  }
  return 'lms';
}

interface UseRecentlyCompletedCompetitionsResult {
  competitions: CompletedCompetition[];
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
}

export function useRecentlyCompletedCompetitions(
  organizationId?: string,
  daysToShow: number = 60
): UseRecentlyCompletedCompetitionsResult {
  const { currentOrganization } = useAuth();
  const [competitions, setCompetitions] = useState<CompletedCompetition[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const isMountedRef = useRef(true);

  const effectiveOrgId = organizationId || currentOrganization?.id;

  const fetchCompletedCompetitions = useCallback(async () => {
    if (!effectiveOrgId) {
      setCompetitions([]);
      setLoading(false);
      return;
    }

    // v3: include worldcup type + prefer end_date for completedAt
    const cacheKey = queryCache.generateKey('recently_completed_competitions_v3', {
      orgId: effectiveOrgId,
      days: daysToShow
    });

    try {
      setLoading(true);
      setError(null);

      const transformed = await queryCache.deduplicate<CompletedCompetition[]>(
        cacheKey,
        async () => {
          // Calculate the cutoff date
          const cutoffDate = new Date();
          cutoffDate.setDate(cutoffDate.getDate() - daysToShow);

          // Fetch finished competitions
          const { data: competitionsData, error: competitionsError } = await supabase
            .from('competitions')
            .select(`
              id,
              name,
              competition_type,
              completion_type,
              winners,
              final_gameweek,
              end_date,
              updated_at,
              calculated_prize_pot,
              prize_pot_currency
            `)
            .eq('organization_id', effectiveOrgId)
            .eq('status', 'finished')
            .gte('updated_at', cutoffDate.toISOString())
            .order('updated_at', { ascending: false })
            .limit(10);

          if (competitionsError) {
            throw competitionsError;
          }

          if (!competitionsData || competitionsData.length === 0) {
            return [];
          }

          // Collect all winner IDs from all competitions
          const allWinnerIds = competitionsData.flatMap(c => c.winners || []);
          const uniqueWinnerIds = [...new Set(allWinnerIds)];

          // Fetch winner details if there are any
          let winnersMap: Record<string, WinnerInfo> = {};
          if (uniqueWinnerIds.length > 0) {
            const { data: usersData, error: usersError } = await supabase
              .from('public_user_profiles')
              .select('id, first_name, last_name')
              .in('id', uniqueWinnerIds);

            if (usersError) {
              console.warn('Error fetching winner details:', usersError);
            } else if (usersData) {
              winnersMap = usersData.reduce((acc, user) => {
                acc[user.id] = {
                  id: user.id,
                  firstName: user.first_name || '',
                  lastName: user.last_name || ''
                };
                return acc;
              }, {} as Record<string, WinnerInfo>);
            }
          }

          // Transform the data
          return competitionsData.map(comp => ({
            id: comp.id,
            name: comp.name,
            competitionType: normalizeCompletedCompetitionType(comp.competition_type),
            completionType: comp.completion_type as CompletedCompetition['completionType'],
            winners: (comp.winners || []).map((winnerId: string) =>
              winnersMap[winnerId] || { id: winnerId, firstName: 'Unknown', lastName: 'User' }
            ),
            finalGameweek: comp.final_gameweek,
            completedAt: new Date(comp.end_date || comp.updated_at),
            prizePot: comp.calculated_prize_pot,
            prizeCurrency: comp.prize_pot_currency
          }));
        },
        CACHE_TTL.MEDIUM // 5 minutes - finished competitions can be reverted by an admin, keep this responsive
      );

      // Ensure Date objects (cache serializes through JSON)
      const ensured = transformed.map(c => ({
        ...c,
        completedAt: c.completedAt instanceof Date ? c.completedAt : new Date(c.completedAt)
      }));

      if (isMountedRef.current) {
        setCompetitions(ensured);
        setLoading(false);
      }
    } catch (err) {
      console.error('Error fetching completed competitions:', err);
      if (isMountedRef.current) {
        setError(err instanceof Error ? err.message : 'Failed to load completed competitions');
        setLoading(false);
      }
    }
  }, [effectiveOrgId, daysToShow]);

  useEffect(() => {
    isMountedRef.current = true;
    fetchCompletedCompetitions();

    return () => {
      isMountedRef.current = false;
    };
  }, [fetchCompletedCompetitions]);

  return {
    competitions,
    loading,
    error,
    refresh: fetchCompletedCompetitions
  };
}
